import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface BreadcrumbItem {
	label: string
	path?: string
}

interface BreadcrumbContextValue {
	breadcrumbs: BreadcrumbItem[]
	setBreadcrumbs: (items: BreadcrumbItem[]) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined)

export const BreadcrumbProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([])

	return (
		<BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
			{children}
		</BreadcrumbContext.Provider>
	)
}

export const useBreadcrumbContext = () => {
	const context = useContext(BreadcrumbContext)
	if (!context) {
		throw new Error('useBreadcrumbContext must be used within BreadcrumbProvider')
	}
	return context
}

/**
 * Hook to set breadcrumbs for a page
 * Automatically cleans up on unmount
 *
 * @example
 * useBreadcrumbs([
 *   { label: 'My Trips', path: '/trips' },
 *   { label: trip.name, path: `/trips/${tripId}` },
 *   { label: 'Itinerary' }
 * ])
 */
export const useBreadcrumbs = (items: BreadcrumbItem[]) => {
	const { setBreadcrumbs } = useBreadcrumbContext()

	useEffect(() => {
		setBreadcrumbs(items)

		// Cleanup: clear breadcrumbs when component unmounts
		return () => {
			setBreadcrumbs([])
		}
	}, [JSON.stringify(items), setBreadcrumbs])
}

